import React from "react";

const TRADEMARK = "\u2122";
const BRAND = `ClearBadge${TRADEMARK}`;
const lastUpdated = "March 6, 2026";

const Section = ({ id, title, children }) => (
  <section id={id} className="legal-section">
    <h2>{title}</h2>
    {children}
  </section>
);

const Subsection = ({ title, children }) => (
  <div className="legal-subsection">
    <h3>{title}</h3>
    {children}
  </div>
);

export default function PrivacyPolicy() {
  return (
    <article className="legal-card" aria-label={`${BRAND} Privacy Policy`}>
      <p className="legal-updated">Last updated: {lastUpdated}</p>
      <p>
        This Privacy Policy explains how {BRAND} (&quot;{BRAND},&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;)
        collects, uses, and shares information when you use our websites, mobile applications, and related services
        (collectively, the &quot;Services&quot;).
      </p>
      <p>
        {BRAND} operates an online directory and verification platform for licensed professionals and provides related
        tools including digital business cards and trust session features.
      </p>

      <Section id="scope" title="1. Scope of this Policy">
        <p>This Privacy Policy applies to:</p>
        <ul className="legal-list">
          <li>Visitors who browse or search {BRAND}</li>
          <li>Licensed professionals who claim or manage profiles</li>
          <li>Users who interact with professionals through the {BRAND} platform</li>
        </ul>
        <p>
          {BRAND} currently provides services primarily to users located in the United States and does not
          intentionally market to individuals located in the European Economic Area or United Kingdom.
        </p>
      </Section>

      <Section id="information" title="2. Information We Collect">
        <p>We collect information in three primary ways.</p>

        <Subsection title="2.1 Information You Provide to Us">
          <p>You may provide information when you:</p>
          <ul className="legal-list">
            <li>Create or claim a {BRAND} profile</li>
            <li>Sign up for subscriptions or paid services</li>
            <li>Request professional verification</li>
            <li>Contact us for support</li>
            <li>Submit feedback or communications</li>
          </ul>
          <p>Information may include:</p>
          <ul className="legal-list">
            <li>Name</li>
            <li>Email address</li>
            <li>Phone number</li>
            <li>Mailing address</li>
            <li>Professional license details</li>
            <li>Business name and contact information</li>
            <li>Profile content (photo, biography, services, links)</li>
            <li>Account credentials</li>
            <li>Billing and payment information processed through third-party payment processors</li>
          </ul>
          <p>{BRAND} does not knowingly collect personal information from children under 13.</p>
        </Subsection>

        <Subsection title="2.2 Information from Public Sources">
          <p>To support our verification directory, we may collect publicly available information from:</p>
          <ul className="legal-list">
            <li>Government licensing databases</li>
            <li>Regulatory agency records</li>
            <li>Public business listings</li>
            <li>Commercial data vendors</li>
          </ul>
          <p>This information may include:</p>
          <ul className="legal-list">
            <li>Professional name</li>
            <li>License type and issuing state</li>
            <li>License number</li>
            <li>License status</li>
            <li>Business contact information</li>
          </ul>
          <p>This data may be used to create profiles labeled &quot;Not {BRAND} Verified.&quot;</p>
        </Subsection>

        <Subsection title="2.3 Automatically Collected Information">
          <p>When using the Services we may collect technical information such as:</p>
          <ul className="legal-list">
            <li>IP address</li>
            <li>Browser type</li>
            <li>Device information</li>
            <li>Pages visited</li>
            <li>Links clicked</li>
            <li>Referring URLs</li>
            <li>Date and time of access</li>
          </ul>
          <p>This information helps us improve the platform and maintain security.</p>
        </Subsection>
      </Section>

      <Section id="use" title="3. How We Use Information">
        <p>We use information for the following purposes.</p>

        <Subsection title="3.1 Providing the Services">
          <ul className="legal-list">
            <li>Display professional profiles</li>
            <li>Enable license verification searches</li>
            <li>Provide digital business card functionality</li>
            <li>Manage subscriptions and billing</li>
            <li>Provide support services</li>
          </ul>
        </Subsection>

        <Subsection title="3.2 Maintaining Directory Accuracy">
          <ul className="legal-list">
            <li>Verify professional identity</li>
            <li>Cross-reference licensing data</li>
            <li>Update license status information</li>
            <li>Allow professionals to claim and manage profiles</li>
          </ul>
        </Subsection>

        <Subsection title="3.3 Platform Improvement and Security">
          <ul className="legal-list">
            <li>Monitor system performance</li>
            <li>Prevent fraud and abuse</li>
            <li>Detect unauthorized activity</li>
            <li>Improve product functionality</li>
          </ul>
        </Subsection>

        <Subsection title="3.4 Communications">
          <p>We may communicate with users to:</p>
          <ul className="legal-list">
            <li>Send account notifications</li>
            <li>Provide verification updates</li>
            <li>Deliver service announcements</li>
            <li>Respond to support requests</li>
          </ul>
          <p>
            We may also send marketing communications where permitted by law. Users can opt out of marketing emails at
            any time.
          </p>
        </Subsection>

        <Subsection title="3.5 SMS Messaging and Mobile Information">
          <p>
            {BRAND} may send one-time transactional SMS messages containing secure trust session links when a user requests a trust session through the {BRAND} platform and explicitly consents to receive the message during an interaction with a verified professional.
          </p>
          <p>
            Users initiate the request to receive the SMS and provide their mobile number for the purpose of receiving the secure trust session link.
          </p>
          <p>
            SMS messages are sent only after a user has provided explicit consent to receive a one-time trust session
            link.
          </p>
          <p>Message frequency varies. In most cases users will receive one SMS message per trust session request.</p>
          <p>Message and data rates may apply.</p>
          <p>Users may reply STOP at any time to opt out of SMS messages or reply HELP for assistance.</p>
          <p>
            Mobile opt-in data and consent information collected for SMS messaging will not be shared with third
            parties or affiliates for marketing or promotional purposes.
          </p>
          <p>
            SMS messages are strictly limited to the specific interaction for which they were requested and are not
            used for ongoing communication.
          </p>
          <p>
            {BRAND} may use service providers to facilitate SMS delivery and related platform operations. These
            providers are authorized to use personal information only as necessary to provide services on behalf of
            {BRAND} and are not permitted to use such information for their own marketing purposes.
          </p>
        </Subsection>
      </Section>

      <Section id="sharing" title="4. How We Share Information">
        <Subsection title="4.1 Public Profiles">
          <p>Information contained in professional profiles may be publicly visible within the {BRAND} directory including:</p>
          <ul className="legal-list">
            <li>Professional name</li>
            <li>License details</li>
            <li>Business contact information</li>
            <li>Profile content chosen by the professional</li>
          </ul>
          <p>Profiles may be labeled as {BRAND} Verified or Not {BRAND} Verified.</p>
        </Subsection>

        <Subsection title="4.2 Service Providers">
          <p>We may share information with service providers who assist with:</p>
          <ul className="legal-list">
            <li>Cloud hosting</li>
            <li>Payment processing</li>
            <li>Email and SMS delivery</li>
            <li>Analytics and monitoring</li>
            <li>Customer support</li>
          </ul>
          <p>
            These providers are authorized to use personal information only as necessary to provide services on our
            behalf and may not use personal information, including phone numbers or SMS consent data, for marketing
            purposes.
          </p>
        </Subsection>

        <Subsection title="4.3 Legal Compliance">
          <p>We may disclose information if required to:</p>
          <ul className="legal-list">
            <li>Comply with legal obligations</li>
            <li>Enforce our Terms of Service</li>
            <li>Protect the safety and security of our users</li>
          </ul>
        </Subsection>

        <Subsection title="4.4 Business Transfers">
          <p>
            In the event of a merger, acquisition, restructuring, or sale of assets, information may be transferred as
            part of that transaction.
          </p>
        </Subsection>

        <Subsection title="4.5 Aggregated Data">
          <p>We may share aggregated or anonymized data that cannot reasonably identify individuals.</p>
        </Subsection>
      </Section>

      <Section id="cookies" title="5. Cookies and Analytics">
        <p>ClearBadge uses cookies and similar technologies to:</p>
        <ul className="legal-list">
          <li>Remember user preferences</li>
          <li>Analyze usage patterns</li>
          <li>Improve platform performance</li>
        </ul>
        <p>Users may adjust cookie settings through their browser.</p>
      </Section>

      <Section id="choices" title="6. User Rights and Choices">
        <p>Users may have rights including:</p>
        <ul className="legal-list">
          <li>Access to personal data</li>
          <li>Correction of inaccurate information</li>
          <li>Deletion requests (subject to legal requirements)</li>
          <li>Opt-out of marketing communications</li>
        </ul>
        <p>Licensed professionals may contact us to claim profiles or request corrections.</p>
      </Section>

      <Section id="security" title="7. Data Security">
        <p>
          We use reasonable administrative, technical, and organizational safeguards to protect information. However,
          no internet transmission method is completely secure.
        </p>
      </Section>

      <Section id="retention" title="8. Data Retention">
        <p>We retain information only as long as necessary to:</p>
        <ul className="legal-list">
          <li>Provide services</li>
          <li>Maintain verification records</li>
          <li>Comply with legal obligations</li>
          <li>Resolve disputes</li>
        </ul>
      </Section>

      <Section id="international" title="9. International Visitors">
        <p>
          {BRAND} operates primarily within the United States. If you access the Services from outside the United
          States, information may be transferred to servers located in the United States.
        </p>
      </Section>

      <Section id="changes" title="10. Changes to This Policy">
        <p>We may update this Privacy Policy periodically. The &quot;Last Updated&quot; date will be revised when changes occur.</p>
      </Section>

      <Section id="contact" title="11. Contact Us">
        <p>If you have questions regarding this Privacy Policy or your personal data, please contact us at:</p>
        <ul className="legal-list">
          <li>privacy@clearbadge.me</li>
        </ul>
      </Section>
    </article>
  );
}
